import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme } from '@mui/material';

const ROIOverlay = ({
    scale = 1,
    rois = [],
    currentPolygon = [],
    isDrawingMode = false,
    drawingMode = null,
    rectangleStart = null,
    isDeleteMode = false,
    isROIVisible = true,
    selectedRoomId = null,
    onPolygonPointAdd,
    onRectangleStart,
    onRectangleComplete,
    onROIDelete,
    wasPanningRef,
    style = {},
}) => {
    const theme = useTheme();
    const [dragPosition, setDragPosition] = useState(null); // Track mouse position for rectangle preview

    // Clear drag position when rectangle is cancelled (e.g., ESC key pressed)
    useEffect(() => {
        if (!rectangleStart) {
            setDragPosition(null);
        }
    }, [rectangleStart]);

    // Calculate dynamic sizes based on zoom scale (inverse scaling like pins)
    const getScaledSize = (baseSize) => {
        // Use inverse scaling but with reasonable limits
        const scaledSize = baseSize / Math.max(scale, 0.5); // Don't scale beyond 2x when zoomed out
        // Clamp to reasonable bounds
        return Math.max(0.001, Math.min(scaledSize, 0.01));
    };

    // Handle mouse move for rectangle preview
    const handleMouseMove = (e) => {
        // Show phantom rectangle preview if first corner is set
        if (isDrawingMode && drawingMode === 'rectangle' && rectangleStart) {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const y = (e.clientY - rect.top) / rect.height;
            setDragPosition([x, y]);
        }
    };

    // Handle click events for rectangle, polygon drawing, or ROI deletion
    const handleClick = (e) => {
        // Check if user was panning (same logic as pin dropping)
        if (wasPanningRef && wasPanningRef.current) {
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        // Handle rectangle mode with two clicks
        if (isDrawingMode && drawingMode === 'rectangle') {
            if (!rectangleStart && onRectangleStart) {
                // First click - set first corner
                onRectangleStart([x, y]);
            } else if (rectangleStart && onRectangleComplete) {
                // Second click - complete rectangle
                onRectangleComplete([x, y]);
                setDragPosition(null); // Clear preview
            }
            return;
        }

        // Handle polygon mode
        if (isDrawingMode && drawingMode === 'polygon' && onPolygonPointAdd) {
            onPolygonPointAdd([x, y]);
        }
    };

    // Handle ROI click for deletion
    const handleROIClick = (e, roi) => {
        if (!isDeleteMode || !onROIDelete) return;

        // Check if user was panning
        if (wasPanningRef && wasPanningRef.current) {
            return;
        }

        e.stopPropagation(); // Prevent triggering the overlay click
        onROIDelete(roi.id);
    };

    // Create SVG path from polygon points (using 0-1 coordinate system)
    const createPolygonPath = (polygon) => {
        if (polygon.length < 3) return '';

        let path = `M ${polygon[0][0]} ${polygon[0][1]}`;
        for (let i = 1; i < polygon.length; i++) {
            path += ` L ${polygon[i][0]} ${polygon[i][1]}`;
        }
        path += ' Z';
        return path;
    };

    // Render a single ROI polygon
    const renderROI = (roi) => {
        if (!roi.polygon || roi.polygon.length < 3) return null;

        const pathData = createPolygonPath(roi.polygon);
        const isSelected = selectedRoomId === roi.id;

        // Calculate centroid for label positioning
        const centroidX =
            roi.polygon.reduce((sum, point) => sum + point[0], 0) / roi.polygon.length;
        const centroidY =
            roi.polygon.reduce((sum, point) => sum + point[1], 0) / roi.polygon.length;

        // Compute polygon bounding box to determine wrap width
        const getPolygonBoundingBox = (polygon) => {
            let minX = Number.POSITIVE_INFINITY;
            let minY = Number.POSITIVE_INFINITY;
            let maxX = Number.NEGATIVE_INFINITY;
            let maxY = Number.NEGATIVE_INFINITY;
            for (const [x, y] of polygon) {
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
            }
            return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
        };

        const bbox = getPolygonBoundingBox(roi.polygon);

        // Estimate text wrapping based on polygon width and current font size
        const fontSize = getScaledSize(0.01);
        const lineHeight = fontSize * 1.2;
        const maxLabelWidth = Math.max(0.005, bbox.width * 0.9); // keep padding inside polygon

        // Approximate text width: characters * fontSize * factor
        const wrapTextToLines = (
            text,
            maxWidth,
            fontSizeUnits,
            charWidthFactor = 0.55,
            maxLines = 5
        ) => {
            if (!text) return [''];

            const words = text.split(/\s+/);
            const lines = [];
            let currentLine = '';

            const measure = (s) => s.length * fontSizeUnits * charWidthFactor;

            const pushCurrent = () => {
                if (currentLine) {
                    lines.push(currentLine.trim());
                    currentLine = '';
                }
            };

            for (let i = 0; i < words.length; i++) {
                const word = words[i];
                const tentative = currentLine ? `${currentLine} ${word}` : word;
                if (measure(tentative) <= maxWidth) {
                    currentLine = tentative;
                } else {
                    // If single word too long, hard-break the word
                    if (!currentLine) {
                        let chunk = '';
                        for (const ch of word) {
                            const next = chunk + ch;
                            if (measure(next) <= maxWidth) {
                                chunk = next;
                            } else {
                                if (chunk) lines.push(chunk);
                                chunk = ch;
                                if (lines.length >= maxLines - 1) break;
                            }
                        }
                        if (chunk) currentLine = chunk;
                    } else {
                        pushCurrent();
                        currentLine = word;
                    }

                    if (lines.length >= maxLines - 1) {
                        // Fill remaining as last line
                        break;
                    }
                }
            }

            pushCurrent();

            // If we exceeded max lines, truncate and add ellipsis
            if (lines.length > maxLines) {
                const limited = lines.slice(0, maxLines);
                const last = limited[maxLines - 1];
                const ellipsis = '…';
                const available = maxWidth - measure(ellipsis);
                let truncated = '';
                for (const ch of last) {
                    if (measure(truncated + ch) <= available) truncated += ch;
                    else break;
                }
                limited[maxLines - 1] = `${truncated}${ellipsis}`;
                return limited;
            }

            return lines;
        };

        // Determine how many lines can reasonably fit vertically
        const maxLinesByHeight = Math.max(1, Math.floor(bbox.height / lineHeight));
        const maxLines = Math.min(5, maxLinesByHeight);
        const lines = wrapTextToLines(roi.name, maxLabelWidth, fontSize, 0.55, maxLines);

        // Determine colors based on state
        const getFillColor = () => {
            if (isDeleteMode) return theme.palette.error.main;
            if (isSelected) return theme.palette.success.main;
            return theme.palette.primary.main;
        };

        const getStrokeColor = () => {
            if (isDeleteMode) return theme.palette.error.main;
            if (isSelected) return theme.palette.success.main;
            return theme.palette.primary.main;
        };

        const getFillOpacity = () => {
            if (isDeleteMode) return 0.3;
            if (isSelected) return 0.25;
            return 0.15;
        };

        const getStrokeWidth = () => {
            return isSelected ? getScaledSize(0.002) : getScaledSize(0.001);
        };

        return (
            <g key={roi.id}>
                <path
                    d={pathData}
                    fill={getFillColor()}
                    fillOpacity={getFillOpacity()}
                    stroke={getStrokeColor()}
                    strokeWidth={getStrokeWidth()}
                    strokeDasharray={
                        isSelected ? undefined : `${getScaledSize(0.004)},${getScaledSize(0.002)}`
                    }
                    style={{
                        cursor: isDeleteMode ? 'pointer' : isDrawingMode ? 'crosshair' : 'default',
                        opacity: isDeleteMode ? 0.9 : isSelected ? 1 : 0.7,
                        pointerEvents: isDeleteMode ? 'auto' : 'none',
                    }}
                    onClick={(e) => handleROIClick(e, roi)}
                />

                {/* ROI Label with wrapping */}
                <text
                    x={centroidX}
                    y={centroidY}
                    fill="#000000"
                    fontSize={fontSize}
                    fontFamily={theme.typography.fontFamily}
                    fontWeight="700"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                        pointerEvents: 'none',
                        opacity: 1,
                    }}
                >
                    {lines.map((line, index) => (
                        <tspan
                            key={`line-${index}`}
                            x={centroidX}
                            dy={index === 0 ? -((lines.length - 1) * lineHeight) / 2 : lineHeight}
                        >
                            {line}
                        </tspan>
                    ))}
                </text>
            </g>
        );
    };

    // Render rectangle preview during drag
    const renderRectanglePreview = () => {
        if (!isDrawingMode || drawingMode !== 'rectangle' || !rectangleStart || !dragPosition) {
            return null;
        }

        const [x1, y1] = rectangleStart;
        const [x2, y2] = dragPosition;

        // Calculate rectangle dimensions
        const x = Math.min(x1, x2);
        const y = Math.min(y1, y2);
        const width = Math.abs(x2 - x1);
        const height = Math.abs(y2 - y1);

        return (
            <g>
                {/* Rectangle preview */}
                <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={theme.palette.primary.main}
                    fillOpacity={0.15}
                    stroke={theme.palette.primary.main}
                    strokeWidth={getScaledSize(0.002)}
                    strokeDasharray={`${getScaledSize(0.006)},${getScaledSize(0.003)}`}
                />

                {/* Corner markers */}
                <circle
                    cx={rectangleStart[0]}
                    cy={rectangleStart[1]}
                    r={getScaledSize(0.003)}
                    fill={theme.palette.secondary.main}
                    stroke={theme.palette.secondary.contrastText}
                    strokeWidth={getScaledSize(0.001)}
                />
                <circle
                    cx={dragPosition[0]}
                    cy={dragPosition[1]}
                    r={getScaledSize(0.003)}
                    fill={theme.palette.secondary.main}
                    stroke={theme.palette.secondary.contrastText}
                    strokeWidth={getScaledSize(0.001)}
                />
            </g>
        );
    };

    // Render current drawing polygon
    const renderCurrentPolygon = () => {
        if (!isDrawingMode || drawingMode !== 'polygon' || currentPolygon.length === 0) return null;

        return (
            <g>
                {/* Current polygon points */}
                {currentPolygon.map((point, index) => (
                    <circle
                        key={index}
                        cx={point[0]}
                        cy={point[1]}
                        r={getScaledSize(0.003)}
                        fill={theme.palette.secondary.main}
                        stroke={theme.palette.secondary.contrastText}
                        strokeWidth={getScaledSize(0.001)}
                    />
                ))}

                {/* Lines connecting points */}
                {currentPolygon.length > 1 && (
                    <path
                        d={createPolygonPath(currentPolygon)}
                        fill="none"
                        stroke={theme.palette.secondary.main}
                        strokeWidth={getScaledSize(0.002)}
                        strokeDasharray={`${getScaledSize(0.006)},${getScaledSize(0.003)}`}
                    />
                )}

                {/* Point labels */}
                {currentPolygon.map((point, index) => (
                    <text
                        key={`label-${index}`}
                        x={point[0] - getScaledSize(0.003)}
                        y={point[1] - getScaledSize(0.003)}
                        fill="#ff0000"
                        fontSize={getScaledSize(0.006)}
                        fontFamily={theme.typography.fontFamily}
                        fontWeight="bold"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{
                            pointerEvents: 'none',
                            textShadow: '0 0 2px rgba(255, 255, 255, 0.8)',
                        }}
                    >
                        {index + 1}
                    </text>
                ))}
            </g>
        );
    };

    // Don't render if no ROIs and not in drawing or delete mode
    if (!isROIVisible && !isDrawingMode && !isDeleteMode) return null;

    // Always show existing ROIs when in drawing or delete mode, regardless of isROIVisible setting
    const shouldShowROIs = isROIVisible || isDrawingMode || isDeleteMode;

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: isDrawingMode || isDeleteMode ? 'auto' : 'none',
                cursor: isDeleteMode ? 'pointer' : isDrawingMode ? 'crosshair' : 'default',
                zIndex: 5,

                ...style,
            }}
            onClick={handleClick}
            onMouseMove={handleMouseMove}
        >
            <svg
                width="100%"
                height="100%"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                }}
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
            >
                {/* Render existing ROIs */}
                {shouldShowROIs && rois.map(renderROI)}

                {/* Render current drawing polygon */}
                {renderCurrentPolygon()}

                {/* Render rectangle preview */}
                {renderRectanglePreview()}
            </svg>
        </Box>
    );
};

ROIOverlay.propTypes = {
    scale: PropTypes.number,
    rois: PropTypes.array,
    currentPolygon: PropTypes.array,
    isDrawingMode: PropTypes.bool,
    drawingMode: PropTypes.string,
    rectangleStart: PropTypes.array,
    isDeleteMode: PropTypes.bool,
    isROIVisible: PropTypes.bool,
    selectedRoomId: PropTypes.string,
    onPolygonPointAdd: PropTypes.func,
    onRectangleStart: PropTypes.func,
    onRectangleComplete: PropTypes.func,
    onROIDelete: PropTypes.func,
    wasPanningRef: PropTypes.object,
    style: PropTypes.object,
};

export default ROIOverlay;
